import React, { useState } from 'react';
import { isTrialActive, isPaidTier, getTrialCountdownText } from '../services/LicenseService';
import { useLicense } from '../contexts/LicenseContext';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from './UserMenu';

interface HeaderProps {
  onFeatureClick?: (feature: string) => void;
  onLicenseClick?: () => void;
  onDocClick?: (docPath: string) => void;
  onUpgradeClick?: () => void;
  onCancelTrialClick?: () => void;
  onSignInClick?: () => void;  // Opens the unified sign-in dialog (UserMenu's Sign In button)
}

const Header: React.FC<HeaderProps> = ({
  onFeatureClick,
  onLicenseClick,
  onDocClick,
  onUpgradeClick,
  onCancelTrialClick,
  onSignInClick,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { licenseInfo } = useLicense();
  const { state: authState } = useAuth();

  // Pre-sign-in, the unified sign-in dialog (opened via Sign In) covers
  // license entry, so the standalone license buttons would be a redundant
  // second door to the same dialog. They return once the user is signed in,
  // as the license-management / trial-control entry points.
  const isAuthenticated = authState.isAuthenticated;

  // Determine license state for context-aware buttons.
  // Trial controls (Trial badge / Upgrade / Cancel Trial) gate on three conditions to
  // prevent the desync described in docs/NAV_BAR_LICENSE_STATE_DESYNC_FIX.md:
  //   1. The license currently reports an active trial.
  //   2. The trial has not been cancelled (status !== 'CANCELED').
  //   3. The trial date hasn't passed (daysRemaining > 0).
  // Otherwise we fall through to paid-tier / FREE rendering.
  const trial = licenseInfo?.trial;
  const trialActive =
    isTrialActive(licenseInfo) &&
    trial?.status !== 'CANCELED' &&
    (trial?.daysRemaining ?? 0) > 0;
  const paidTier = isPaidTier(licenseInfo);
  const trialCountdown = getTrialCountdownText(licenseInfo);

  const navigation = [
    { name: 'Features', href: '/docs/Feature-Comparison.md' },
    { name: 'Quick Start', href: '/docs/Quick-Start-Guide.md' },
    { name: 'User Guide', href: '/docs/User-Guide.md' },
    { name: 'Documentation', href: '/docs/README.md' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-soft-sm">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className="flex h-16 items-center justify-between">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <a href="/" className="flex items-center space-x-3 group">
              {/* Logo SVG */}
              <div className="relative">
                <div className="absolute inset-0 bg-primary-500/20 rounded-xl blur-xl group-hover:bg-primary-500/30 transition-all duration-300"></div>
                <svg
                  className="relative h-10 w-10 text-primary-600 group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
              </div>
              <div>
                <span className="text-xl font-display font-bold text-neutral-900 group-hover:text-primary-700 transition-colors">
                  PDF2Production
                </span>
              </div>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-6">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => onDocClick?.(item.href)}
                className="text-sm font-medium text-neutral-700 hover:text-primary-600 transition-colors duration-200 relative group"
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-500 group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex md:items-center md:space-x-3">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('pfb-toggle-guide'))}
              className="flex items-center gap-2 text-neutral-700 hover:text-primary-600 text-sm font-medium px-4 py-2 rounded-xl hover:bg-neutral-50 transition-all duration-200"
              title="Step-by-step guide: from sign-in to your generated project"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span>Guide</span>
            </button>

            <button
              onClick={() => window.open('/help-guide.html', '_blank', 'width=1200,height=800,scrollbars=yes')}
              className="flex items-center gap-2 text-neutral-700 hover:text-primary-600 text-sm font-medium px-4 py-2 rounded-xl hover:bg-neutral-50 transition-all duration-200"
              title="Open Help Guide"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Help</span>
            </button>

            {/* Context-aware license buttons — signed-in users only */}
            {isAuthenticated && (trialActive ? (
              <>
                {/* Trial countdown badge */}
                <div className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-800 text-sm font-medium rounded-lg">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Trial: {trialCountdown}</span>
                </div>
                {/* Upgrade button */}
                <button
                  onClick={onUpgradeClick || onLicenseClick}
                  className="flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span>Upgrade</span>
                </button>
                {/* Cancel Trial button */}
                <button
                  onClick={onCancelTrialClick}
                  className="flex items-center gap-2 bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-800 text-sm font-medium px-3 py-2 rounded-xl transition-all duration-200"
                  title="Cancel trial - no charge"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel Trial</span>
                </button>
              </>
            ) : paidTier ? (
              /* Paid tier - Manage License button */
              <button
                onClick={onLicenseClick}
                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Manage License</span>
              </button>
            ) : (
              /* Default - License button (no active license) */
              <button
                onClick={onLicenseClick}
                className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span>License</span>
              </button>
            ))}

            {/* User Menu - Sign In / User Dropdown */}
            <div className="border-l border-neutral-200 pl-3 ml-1">
              <UserMenu onSignInClick={onSignInClick} />
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              type="button"
              className="text-neutral-700 hover:text-primary-600 p-2 rounded-lg hover:bg-neutral-50 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open menu</span>
              {!mobileMenuOpen ? (
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              ) : (
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden animate-slide-down">
            <div className="space-y-1 pb-3 pt-2">
              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => {
                    onDocClick?.(item.href);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 rounded-lg transition-colors"
                >
                  {item.name}
                </button>
              ))}
              <div className="border-t border-neutral-200 pt-3 mt-3 space-y-2">
                <button
                  onClick={() => {
                    window.open('/help-guide.html', '_blank');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full text-neutral-700 hover:text-primary-600 text-base font-medium px-3 py-2 rounded-xl hover:bg-neutral-50 transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Help Guide</span>
                </button>

                {/* Mobile: Sign In pre-auth (the desktop UserMenu is hidden on
                    mobile, so without this the menu had no sign-in entry);
                    context-aware license buttons once signed in */}
                {!isAuthenticated ? (
                  <button
                    onClick={() => {
                      onSignInClick?.();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 w-full bg-blue-600 text-white text-base font-semibold px-3 py-2 rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-md"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign In</span>
                  </button>
                ) : trialActive ? (
                  <>
                    {/* Trial countdown badge */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-800 text-base font-medium rounded-xl">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Trial: {trialCountdown}</span>
                    </div>
                    {/* Upgrade button */}
                    <button
                      onClick={() => {
                        (onUpgradeClick || onLicenseClick)?.();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white text-base font-semibold px-3 py-2 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-md"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                      <span>Upgrade Now</span>
                    </button>
                    {/* Cancel Trial button */}
                    <button
                      onClick={() => {
                        onCancelTrialClick?.();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-800 text-base font-medium px-3 py-2 rounded-xl transition-all duration-200"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Cancel Trial</span>
                    </button>
                  </>
                ) : paidTier ? (
                  /* Paid tier - Manage License */
                  <button
                    onClick={() => {
                      onLicenseClick?.();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 w-full bg-gradient-to-r from-green-500 to-green-600 text-white text-base font-semibold px-3 py-2 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>Manage License</span>
                  </button>
                ) : (
                  /* Default - License button */
                  <button
                    onClick={() => {
                      onLicenseClick?.();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white text-base font-semibold px-3 py-2 rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all duration-200 shadow-md"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <span>License Management</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;